import { CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const Row = ({ c, kind }) => (
  <div className="flex items-baseline gap-2 font-mono text-[11.5px]"
    data-testid={`clear-blockers-change-${c.field}`}>
    <span className="w-[86px] shrink-0 text-[10px] uppercase tracking-wider text-[var(--c-muted)]">
      {kind}
    </span>
    <span className="text-[var(--c-text)]">{c.label}</span>
    <span className="text-[var(--c-muted)]">{String(c.current ?? "—")}</span>
    <ArrowRight size={11} className="text-[var(--c-muted)]" />
    <span className="text-[var(--c-green)]">
      {String(c.suggested)}{c.unit ? ` ${c.unit}` : ""}
    </span>
    <span className="ml-auto text-[10px] text-[var(--c-muted)]">{c.check_id}</span>
  </div>
);

export const ClearBlockersDialog = ({ open, onOpenChange, result }) => {
  if (!result) return null;
  const cleared = result.cleared || [];
  const remaining = result.remaining || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="clear-blockers-dialog"
        className="bg-[var(--c-surface)] border-[var(--c-border)] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[var(--c-text)] text-sm flex items-center gap-2">
            {remaining.length ? <Warning size={16} className="text-[var(--c-amber)]" />
              : <CheckCircle size={16} className="text-[var(--c-green)]" />}
            Clear all blockers — {cleared.length} cleared
            {remaining.length ? `, ${remaining.length} still open` : ", nothing left open"}
          </DialogTitle>
        </DialogHeader>

        <div className="text-[11.5px] text-[var(--c-muted)]" data-testid="clear-blockers-summary">
          Health {result.health_score}/100 · blockers {result.blockers_now} → {result.blockers_after}
          {result.notified && " · the project team was notified of the Intake change"}
          {result.not_considered > 0 && (
            <span className="text-[var(--c-amber)]">
              {" "}· {result.not_considered} further blocker(s) were not searched in this pass — run it again
            </span>
          )}
        </div>

        <div className="mt-3 space-y-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">
            What was changed
          </div>
          {(result.input_changes || []).map((c) => <Row key={c.field} c={c} kind="Design" />)}
          {(result.spec_changes || []).map((c) => <Row key={c.field} c={c} kind="Intake" />)}
          {!(result.input_changes || []).length && !(result.spec_changes || []).length && (
            <div className="text-[11.5px] text-[var(--c-muted)]">Nothing to change.</div>
          )}
        </div>

        {cleared.length > 0 && (
          <div className="mt-4 space-y-1" data-testid="clear-blockers-cleared">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">
              Checks cleared
            </div>
            {cleared.map((c) => (
              <div key={c.check_id} className="flex items-baseline gap-2 text-[11.5px]"
                data-testid={`clear-blockers-cleared-${c.check_id}`}>
                <CheckCircle size={12} className="shrink-0 text-[var(--c-green)]" />
                <span className="font-mono text-[10.5px] text-[var(--c-green)]">{c.check_id}</span>
                <span className="text-[var(--c-text)]">{c.title}</span>
              </div>
            ))}
          </div>
        )}

        {remaining.length > 0 && (
          <div className="mt-4 space-y-1" data-testid="clear-blockers-remaining">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-amber)]">
              Still open — these need a design change, a client-agreed revision or a waiver
            </div>
            {remaining.map((r) => (
              <div key={r.check_id} className="flex items-baseline gap-2 text-[11.5px]"
                data-testid={`clear-blockers-remaining-${r.check_id}`}>
                <Warning size={12} className="shrink-0 text-[var(--c-amber)]" />
                <span className="font-mono text-[10.5px] text-[var(--c-amber)]">{r.check_id}</span>
                <span className="text-[var(--c-text)]">{r.title}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
